import {
  loadFixture, parseBody, nextId, mergeEntity, urlParam, groupIdentifierFromUrl, groupFixtureName,
} from '../mockDataStore';

const CUSTOM_FIELD_DETAIL = /^\/?api\/v1\/groups\/[\w-]+\/custom-group-fields\/([\w-]+)\/?$/;
const CUSTOM_FIELD_VALUE = /^\/?api\/v1\/groups\/[\w-]+\/custom-group-fields\/([\w-]+)\/value\/?$/;
const GROUP_USER_DETAIL = /^\/?api\/v1\/groups\/[\w-]+\/users\/(\d+)\/?$/;
const GROUP_DETAIL = /^\/?api\/v1\/groups\/([\w-]+)\/?$/;

async function setCustomFieldValue(config) {
  const fieldSlug = urlParam(config, CUSTOM_FIELD_VALUE);
  const customFields = await loadFixture('groups/custom-fields');
  const field = customFields.results.find((item) => item.slug === fieldSlug);
  if (!field) return [404, { message: 'Not found.' }];
  Object.assign(field, parseBody(config));
  return [200, field];
}

export default function registerGroupMocks(mock) {
  mock.onGet(/^\/?api\/v1\/groups\/self\/?$/).reply(async () => {
    const selfGroups = await loadFixture('groups/self-groups');
    return [200, selfGroups];
  });

  mock.onGet(/^\/?api\/v1\/[\w-]+\/feature-flags\/?$/).reply(async () => {
    const featureFlags = await loadFixture('groups/feature-flags');
    return [200, featureFlags];
  });

  mock.onGet(/^\/?api\/v1\/groups\/[\w-]+\/config\/?$/).reply(async () => {
    const groupConfig = await loadFixture('groups/group-config');
    return [200, groupConfig];
  });

  mock.onPatch(/^\/?api\/v1\/groups\/[\w-]+\/config\/?$/).reply(async (config) => {
    const groupConfig = await loadFixture('groups/group-config');
    Object.assign(groupConfig, parseBody(config));
    return [200, groupConfig];
  });

  mock.onGet(/^\/?api\/v1\/groups\/[\w-]+\/subscription-status\/?$/).reply(() => [
    200,
    { is_active: true },
  ]);

  mock.onGet(/^\/?api\/v1\/groups\/[\w-]+\/custom-group-fields\/?$/).reply(async () => {
    const customFields = await loadFixture('groups/custom-fields');
    return [200, customFields];
  });

  mock.onPost(/^\/?api\/v1\/groups\/[\w-]+\/custom-group-fields\/?$/).reply(async (config) => {
    const customFields = await loadFixture('groups/custom-fields');
    const created = { id: nextId(customFields.results), ...parseBody(config) };
    customFields.results.push(created);
    customFields.count += 1;
    return [201, created];
  });

  mock.onPut(CUSTOM_FIELD_DETAIL).reply(async (config) => {
    const fieldSlug = urlParam(config, CUSTOM_FIELD_DETAIL);
    const customFields = await loadFixture('groups/custom-fields');
    const field = customFields.results.find((item) => item.slug === fieldSlug);
    if (!field) return [404, { message: 'Not found.' }];
    Object.assign(field, parseBody(config));
    return [200, field];
  });

  mock.onDelete(CUSTOM_FIELD_DETAIL).reply(async (config) => {
    const fieldSlug = urlParam(config, CUSTOM_FIELD_DETAIL);
    const customFields = await loadFixture('groups/custom-fields');
    customFields.results = customFields.results.filter((item) => item.slug !== fieldSlug);
    customFields.count = customFields.results.length;
    return [200, {}];
  });

  mock.onPost(CUSTOM_FIELD_VALUE).reply(setCustomFieldValue);
  mock.onPut(CUSTOM_FIELD_VALUE).reply(setCustomFieldValue);
  mock.onDelete(CUSTOM_FIELD_VALUE).reply(async (config) => {
    const fieldSlug = urlParam(config, CUSTOM_FIELD_VALUE);
    const customFields = await loadFixture('groups/custom-fields');
    const field = customFields.results.find((item) => item.slug === fieldSlug);
    if (!field) return [404, { message: 'Not found.' }];
    field.value = null;
    return [200, field];
  });

  mock.onPatch(GROUP_USER_DETAIL).reply(async (config) => {
    const userId = Number(urlParam(config, GROUP_USER_DETAIL));
    const groupId = groupIdentifierFromUrl(config.url);
    const profiles = await loadFixture(groupFixtureName('groups/profiles', groupId));
    const profile = profiles.results.find((item) => item.id === userId);
    if (!profile) return [404, { message: 'Not found.' }];
    const body = parseBody(config);
    // setGroupAdmin sends `is_admin` in the request, but a Profile's own field (read
    // elsewhere as `isGroupAdmin`, e.g. GroupDescription.vue's admin list) is
    // `is_group_admin` — translate rather than let a stray `is_admin` key get added.
    if ('is_admin' in body) {
      profile.is_group_admin = body.is_admin;
      delete body.is_admin;
    }
    Object.assign(profile, body);
    return [200, profile];
  });

  mock.onPost(/^\/?api\/v1\/groups\/[\w-]+\/delete_cover_photo\/?$/).reply(async (config) => {
    const groupId = groupIdentifierFromUrl(config.url);
    const group = await loadFixture(groupFixtureName('groups/group', groupId));
    group.group_photo = null;
    return [200, group];
  });

  mock.onPost(/^\/?api\/v1\/groups\/[\w-]+\/files\/?$/).reply(() => [
    201,
    { file: '/mock-data/assets/uploads/demo-upload.jpg' },
  ]);

  mock.onPost(/^\/?api\/v1\/groups\/[\w-]+\/invite_users\/?$/).reply(() => [200, {}]);

  mock.onPost(/^\/?api\/v1\/groups\/[\w-]+\/request-subscription\/?$/).reply(() => [200, {}]);

  mock.onPost(/^\/?api\/v1\/groups\/[\w-]+\/transfer-profile\/?$/).reply(() => [200, {}]);

  mock.onPost(/^\/?api\/v1\/profiles\/complete-transfer\/?$/).reply(() => [200, {}]);

  mock.onGet(/^\/?api\/v1\/payment-providers\/?$/).reply(() => [200, []]);

  mock.onPost(/^\/?api\/v1\/validate-recaptcha\/?$/).reply(() => [200, { success: true }]);

  mock.onPost(/^\/?api\/v1\/groups\/?$/).reply(async (config) => {
    const group = await loadFixture('groups/group');
    const created = { ...group, ...parseBody(config) };
    return [201, created];
  });

  // Group detail must be registered last. Its pattern (`/groups/{slug}/`, nothing after)
  // is anchored with `$`, so it can never match a longer `/groups/...` path above it — but
  // `/groups/self` (registered first, above) has the same shape (one path segment) and
  // would otherwise collide, since MockAdapter uses the first registered handler whose
  // pattern matches. Registration order is what keeps `self` from falling through to here.
  mock.onGet(GROUP_DETAIL).reply(async (config) => {
    const slug = urlParam(config, GROUP_DETAIL);
    const group = await loadFixture(groupFixtureName('groups/group', slug));
    return [200, group];
  });

  mock.onPatch(GROUP_DETAIL).reply(async (config) => {
    const slug = urlParam(config, GROUP_DETAIL);
    const group = await loadFixture(groupFixtureName('groups/group', slug));
    mergeEntity(group, parseBody(config), ['group_photo']);
    return [200, group];
  });
}
